<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiDrawer - Design System Drawer/Sheet Component
 *
 * A slide-in panel for supplementary content that doesn't block the main workflow.
 *
 * WHEN TO USE DRAWER (vs Modal vs ActionSheet):
 * - Filters and settings panels
 * - Detail views (item details, user profiles)
 * - Secondary forms or editors
 * - Content user might reference while working on main page
 * - Navigation menus on mobile
 *
 * SIDE GUIDELINES:
 * - right: Default for detail panels, filters, settings (desktop)
 * - left: Navigation menus, sidebars
 * - bottom: Mobile-friendly sheets, quick actions, previews
 *
 * DON'T USE DRAWER FOR:
 * - Critical confirmations (use Modal)
 * - Simple action choices on mobile (use ActionSheet)
 *
 * @example
 * <UiDrawer v-model="isOpen" side="right" title="Filters">
 *   <FilterForm />
 * </UiDrawer>
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiDrawerProps {
   /** Drawer title */
   title?: string;
   /** Subtitle or meta text */
   subtitle?: string;
   /** Which side the drawer slides from */
   side?: 'bottom' | 'right' | 'left';
   /** Size of the drawer (for left/right) */
   size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
   /** Whether clicking backdrop closes drawer */
   closeOnBackdrop?: boolean;
   /** Whether pressing Escape closes drawer */
   closeOnEscape?: boolean;
   /** Show the pull handle (for bottom sheets) */
   showHandle?: boolean;
   /** Show close button in header */
   showCloseButton?: boolean;
   /** Custom z-index for the drawer */
   zIndex?: number;
}

const props = withDefaults(defineProps<UiDrawerProps>(), {
   side: 'right',
   size: 'md',
   closeOnBackdrop: true,
   closeOnEscape: true,
   showHandle: true,
   showCloseButton: true,
   zIndex: 9999,
});

const emit = defineEmits<{
   close: [];
}>();

const modelValue = defineModel<boolean>({ default: false });

// Refs for focus management
const drawerRef = ref<HTMLDivElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;

// Size classes for left/right drawers
const sizeClasses = computed(() => {
   if (props.side === 'bottom')
      return '';

   const sizes = {
      sm: 'w-80 max-w-[90vw]',
      md: 'w-[28rem] max-w-[90vw]',
      lg: 'w-[36rem] max-w-[90vw]',
      xl: 'w-[48rem] max-w-[90vw]',
      full: 'w-screen',
   };
   return sizes[props.size];
});

// Position classes based on side
const positionClasses = computed(() => {
   switch (props.side) {
      case 'bottom':
         return 'bottom-0 inset-x-0';
      case 'left':
         return 'top-0 left-0 bottom-0';
      case 'right':
      default:
         return 'top-0 right-0 bottom-0';
   }
});

// Transform classes for open/close animation
const openTransform = computed(() => {
   switch (props.side) {
      case 'bottom':
         return 'translate-y-0';
      case 'left':
         return 'translate-x-0';
      case 'right':
      default:
         return 'translate-x-0';
   }
});

// Container styling based on side
const containerClasses = computed(() => {
   const base = 'bg-white shadow-2xl flex flex-col';

   if (props.side === 'bottom') {
      return `${base} rounded-t-2xl max-h-[85vh]`;
   }
   return `${base} h-full`;
});

function close() {
   modelValue.value = false;
   emit('close');
   // Restore focus to previously focused element
   if (previouslyFocusedElement) {
      previouslyFocusedElement.focus();
      previouslyFocusedElement = null;
   }
}

function handleBackdropClick() {
   if (props.closeOnBackdrop) {
      close();
   }
}

function handleEscape(event: KeyboardEvent) {
   if (event.key === 'Escape' && props.closeOnEscape && modelValue.value) {
      close();
   }
}

// Focus trap - trap Tab key within drawer
function handleTabTrap(event: KeyboardEvent) {
   if (event.key !== 'Tab' || !drawerRef.value)
      return;

   const focusableElements = drawerRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
   );
   const firstElement = focusableElements[0];
   const lastElement = focusableElements[focusableElements.length - 1];

   if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
   }
   else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
   }
}

// Lock body scroll and manage focus when drawer is open
watch(modelValue, async (isOpen) => {
   if (isOpen) {
      // Save currently focused element
      previouslyFocusedElement = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleTabTrap);
      // Focus the drawer after it renders
      await nextTick();
      drawerRef.value?.focus();
   }
   else {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleTabTrap);
   }
});

onMounted(() => {
   document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
   document.removeEventListener('keydown', handleEscape);
   document.removeEventListener('keydown', handleTabTrap);
   document.body.style.overflow = '';
});
</script>

<template>
   <Teleport to="body">
      <!-- Backdrop -->
      <Transition
         enter-active-class="duration-300 ease-out"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0"
            :class="side === 'bottom' ? 'bg-black/40' : 'bg-black/20'"
            :style="{ zIndex: zIndex - 1 }"
            @click="handleBackdropClick"
         />
      </Transition>

      <!-- Drawer Container -->
      <div
         v-if="modelValue"
         ref="drawerRef"
         role="dialog"
         aria-modal="true"
         tabindex="-1"
         class="fixed transition-transform duration-300 ease-out outline-none"
         :class="[
            positionClasses,
            sizeClasses,
            openTransform,
         ]"
         :style="{ zIndex }"
         @click.stop
      >
         <!-- Pull Handle (bottom sheet only) -->
         <div
            v-if="side === 'bottom' && showHandle"
            class="flex justify-center pt-2 pb-1"
         >
            <button
               class="w-12 h-1.5 bg-white/40 rounded-full"
               aria-label="Close drawer"
               @click="close"
            />
         </div>

         <!-- Main Container -->
         <div :class="containerClasses">
            <!-- Header -->
            <div
               v-if="title || subtitle || showCloseButton || $slots.header"
               class="flex items-start justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b border-neutral-dark/20 shrink-0"
            >
               <slot name="header">
                  <div class="flex flex-col gap-0.5 min-w-0 flex-1 pr-2">
                     <span v-if="subtitle" class="text-sm text-gray-500 font-medium">
                        {{ subtitle }}
                     </span>
                     <h3
                        v-if="title"
                        class="text-lg font-semibold text-gray-800 truncate"
                        :title="title"
                     >
                        {{ title }}
                     </h3>
                  </div>
               </slot>

               <button
                  v-if="showCloseButton"
                  class="p-2 hover:bg-black/10 rounded-lg transition-colors shrink-0"
                  aria-label="Close drawer"
                  @click="close"
               >
                  <Icon :icon="IconLib.actions.close" class="size-5" />
               </button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto">
               <slot />
            </div>

            <!-- Footer -->
            <div
               v-if="$slots.footer"
               class="border-t border-neutral-dark/20 px-4 sm:px-5 py-3 sm:py-4 shrink-0"
            >
               <slot name="footer" />
            </div>
         </div>
      </div>
   </Teleport>
</template>
